import { Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Circle } from 'lucide-react';
import { Emitente } from '../api/emitente';
import { formatCNPJ } from '../utils/formatters';

interface Props {
  emitente: Emitente;
  rightSlot?: React.ReactNode;
  backTo?: string;
}

export default function EmpresaHeader({ emitente, rightSlot, backTo = '/' }: Props) {
  const ativo = emitente.ativo !== false;
  const nome = emitente.nome_fantasia || emitente.razao_social;
  const cidadeUf = [emitente.xmun, emitente.uf].filter(Boolean).join(' - ');
  const ambiente = emitente.ambiente === 'producao' ? 'producao' : 'homologacao';

  return (
    <div className="card-surface p-3 md:p-4">
      <div className="flex items-start gap-3 md:items-center">
        <Link
          to={backTo}
          className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-100 hover:text-slate-950 dark:border-slate-800 dark:bg-slate-900/70 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-slate-100"
          title="Voltar"
          aria-label="Voltar"
        >
          <ArrowLeft className="h-4 w-4" />
        </Link>

        <div className="min-w-0 flex-1">
          <div className="mb-0.5 flex flex-wrap items-center gap-1.5">
            <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider ${ativo ? 'text-emerald-700 dark:text-emerald-400' : 'text-neutral-500 dark:text-neutral-400'}`}>
              {ativo ? <CheckCircle2 className="h-3 w-3" /> : <Circle className="h-3 w-3" />}
              {ativo ? 'Ativa' : 'Inativa'}
            </span>
            <span className="text-neutral-300 dark:text-neutral-700">/</span>
            {ambiente === 'producao' ? (
              <span className="rounded border border-violet-200 bg-violet-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-violet-800 dark:border-violet-500/30 dark:bg-violet-500/15 dark:text-violet-300" title="Emissões desse emitente vão para a SEFAZ de produção (tem valor fiscal real)">Produção</span>
            ) : (
              <span className="rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/15 dark:text-amber-300" title="Emissões desse emitente vão para homologação (ambiente de teste, sem valor fiscal)">Homologação</span>
            )}
          </div>
          <h1 className="h-display truncate text-lg leading-tight text-slate-950 dark:text-white md:text-2xl" title={nome}>
            {nome}
          </h1>
          <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0 text-xs text-neutral-600 dark:text-neutral-400">
            <span className="mono">{formatCNPJ(emitente.cnpj)}</span>
            {cidadeUf && <span>{cidadeUf}</span>}
            {emitente.ie && <span>IE {emitente.ie}</span>}
          </div>
        </div>

        {rightSlot && (
          <div className="hidden shrink-0 items-center gap-2 sm:flex">
            {rightSlot}
          </div>
        )}
      </div>
      {rightSlot && (
        <div className="mt-2 flex items-center gap-2 border-t border-slate-200 pt-2 dark:border-slate-800 sm:hidden">
          {rightSlot}
        </div>
      )}
    </div>
  );
}
